import { HStack, Box, IconButton, Text } from '@cad-arc';
import React, { useCallback } from 'react';
import { isNumber } from 'lodash';
import { useWatch } from 'react-hook-form';
import { QueryableEntityType } from 'mark43-federated-search';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import {
    EventWorkspacePanelDexFormPrefill,
    EventWorkspacePanelDexNextFormPrefill,
} from '../../../workspace-panel-types';
import { FormInputIconButton } from '../../../../forms/integrated-form-components/form-button';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EntityPanelFormVehicleBaseKey, EntityPanelVehiclesForm } from '../types';
import { useDrawerControls } from '../../../../drawers/components/drawer-context-provider';
import { useOpenDepartmentLink } from '../../../../layout/use-open-department-link';
import {
    useEventNotifications,
    useRemoveEventNotifications,
} from '../../../../event-notifications/use-event-notifications';
import { NotificationCounter } from '../../../../common/components/notification-counter';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { observer } from 'mobx-react-lite';
import { getUniversalSearchFormLabel } from '../../../../common/helpers/get-usx-labels';
import { useHandleOpenInDexButtonClick } from '../../../hooks/use-handle-open-in-dex-button-click';

interface EntityPanelFormVehicleCardControlsProps {
    isNewEntity: boolean;
    allowMembersControl?: boolean;
    remove: UseTrackedFieldArrayRemove;
    baseKey: EntityPanelFormVehicleBaseKey;
    index: number;
    isReadOnly?: boolean;
    entityNumber?: string;
    eventId?: number;
}

export function MasterVehicleLinkButton({
    isPersisted,
    masterVehicleId,
}: {
    isPersisted?: boolean;
    masterVehicleId: number | null | undefined;
}) {
    const openDepartmentLink = useOpenDepartmentLink();
    const enabledFeatures = useEnabledFeatures();
    if (!enabledFeatures.masterProfilesEnabled || !isPersisted || !masterVehicleId) {
        return null;
    }
    return (
        <IconButton
            size="sm"
            style={{ alignSelf: 'flex-start' }}
            icon="OpenNewWindow"
            variant="ghost"
            aria-label="Open Vehicle Profile in RMS"
            onClick={() => {
                openDepartmentLink({
                    path: `/profiles/vehicles/${masterVehicleId}/details`,
                    app: 'RMS',
                    tenantStrategy: 'PREFER_RMS_TENANT',
                });
            }}
            tabIndex={-1}
        />
    );
}

export const EntityPanelFormVehicleCardControls = observer(
    function EntityPanelFormVehicleCardControls({
        remove,
        allowMembersControl,
        isNewEntity,
        baseKey,
        index,
        isReadOnly,
        entityNumber,
        eventId,
    }: EntityPanelFormVehicleCardControlsProps) {
        const { form } = useIntegratedForm<EntityPanelVehiclesForm>();
        const { isFirstResponder, isCad } = useApplicationType();
        const drawerControls = useDrawerControls();
        const enabledFeatures = useEnabledFeatures();

        const handleOpenInDexButtonClick = useHandleOpenInDexButtonClick({
            dexFormQuery: 'VEHICLE_MASTER_QUERY',
            dexNextEntityType: QueryableEntityType.VEHICLE,
            getDexFormValues,
            getDexNextFormValues,
        });

        const plateNumber = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.plateNumber`,
        });

        const vehicleId = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.id`,
        });

        const vehicleContextId = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.contextId`,
        });

        // we use contextId for tow vehicle notifications but id for regular vehicle notifications
        const vehicleNotificationsId = baseKey === 'towVehicles' ? vehicleContextId : vehicleId;

        const isPersisted = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.isPersisted`,
        });

        const eventMasterVehicleData = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.eventMasterVehicleData`,
        });

        const showDexButton = !isNewEntity && isPersisted && isCad && !isReadOnly;
        const showDeleteButton = !isReadOnly && (allowMembersControl || isFirstResponder);

        const getTitle = useCallback(() => {
            let name = `Vehicle`;
            switch (baseKey) {
                case 'vehicles':
                    name = 'Vehicle';
                    break;
                case 'towVehicles':
                    name = 'Tow Vehicle';
                    break;
                case 'mergedVehicles':
                    name = 'Merged Vehicle';
                    break;
                case 'mergedTowVehicles':
                    name = 'Merged Tow Vehicle';
                    break;
                default:
                    break;
            }
            return plateNumber ?? `${name} ${index + 1}`;
        }, [plateNumber, baseKey, index]);

        const getFormValues = () =>
            form.getValues([
                `${baseKey}.${index}.vinNumber`,
                `${baseKey}.${index}.plateNumber`,
                `${baseKey}.${index}.year`,
                `${baseKey}.${index}.stateOption`,
                `${baseKey}.${index}.vehicleMakeOption`,
                `${baseKey}.${index}.colorOption`,
                `${baseKey}.${index}.vehicleModelOption`,
                `${baseKey}.${index}.notes`,
            ]);

        function getDexFormValues(): EventWorkspacePanelDexFormPrefill {
            const [vinNumber, plateNumber, year, stateOption, vehicleMakeOption] = getFormValues();

            return {
                eventNumber: entityNumber,
                vehicle: {
                    vehicleIdentificationNumber: vinNumber ?? undefined,
                    licensePlateNumber: plateNumber ?? undefined,
                    vehicleYear: isNumber(year) ? `${year}` : undefined,
                    state:
                        stateOption && stateOption.label && !stateOption.isOther
                            ? stateOption.label
                            : undefined,
                    vehicleMakeCode: vehicleMakeOption?.ncicCode ?? undefined,
                },
            };
        }

        function getDexNextFormValues(): EventWorkspacePanelDexNextFormPrefill {
            const [
                vinNumber,
                plateNumber,
                year,
                stateOption,
                vehicleMakeOption,
                colorOption,
                vehicleModelOption,
                notes,
            ] = getFormValues();

            return {
                eventNumber: entityNumber,
                entityType: QueryableEntityType.VEHICLE,
                prefillValues: {
                    VehicleIdentificationNumber: vinNumber ?? undefined,
                    LicensePlateNumber: plateNumber ?? undefined,
                    VehicleYear: isNumber(year) ? `${year}` : undefined,
                    RegistrationState: stateOption?.value ? String(stateOption?.value) : undefined,
                    VehicleMakeCode: vehicleMakeOption?.ncicCode,
                    VehicleMakeName: vehicleMakeOption?.value
                        ? String(vehicleMakeOption?.value)
                        : undefined,
                    VehicleColorCode: colorOption?.value ? String(colorOption?.value) : undefined,
                    VehicleModelName: vehicleModelOption?.value
                        ? String(vehicleModelOption?.value)
                        : undefined,
                    VehicleModelCode: vehicleModelOption?.ncicCode,
                    CAD_EVENT_SELECT_VALUE: String(eventId),
                    FreeText: notes ?? undefined,
                },
            };
        }

        function onDelete() {
            form.setValue(`${baseKey}.${index}.isRemoved`, true);
            remove(vehicleId, index);
        }

        const masterVehicleMatchNotifications = useEventNotifications({
            entityId: vehicleNotificationsId ?? undefined,
            notifications: ['VEHICLE_MASTER_SEARCH_RESULTS', 'TOW_VEHICLE_MASTER_SEARCH_RESULTS'],
        });

        const removeEventNotifications = useRemoveEventNotifications();

        return (
            <HStack
                padding={2}
                pl={isNewEntity ? 0 : 1}
                spacing={2}
                width="100%"
                alignItems="center"
                justifyContent="space-between"
            >
                <Text variant="headingSm">{getTitle()}</Text>
                <HStack>
                    {enabledFeatures.masterProfilesEnabled &&
                    isPersisted &&
                    !eventMasterVehicleData?.id ? (
                        <Box position="relative">
                            {masterVehicleMatchNotifications.length > 0 ? (
                                <NotificationCounter
                                    style={{ position: 'absolute', bottom: '-6px', right: '-6px' }}
                                >
                                    {1}
                                </NotificationCounter>
                            ) : undefined}
                            <IconButton
                                size="sm"
                                style={{ alignSelf: 'flex-start' }}
                                icon="Link"
                                variant="ghost"
                                aria-label="Link to Matched Vehicle"
                                onClick={() => {
                                    removeEventNotifications({
                                        entityId: vehicleNotificationsId ?? undefined,
                                        notifications: [
                                            'VEHICLE_MASTER_SEARCH_RESULTS',
                                            'TOW_VEHICLE_MASTER_SEARCH_RESULTS',
                                        ],
                                    });
                                    if (vehicleId) {
                                        drawerControls.open({
                                            type: 'master-profile-matches',
                                            props: {
                                                entityId: vehicleId,
                                                entityType: 'VEHICLE',
                                            },
                                        });
                                    }
                                }}
                                tabIndex={-1}
                            />
                        </Box>
                    ) : undefined}{' '}
                    <MasterVehicleLinkButton
                        isPersisted={isPersisted}
                        masterVehicleId={eventMasterVehicleData?.id}
                    />
                    {showDexButton && (
                        <IconButton
                            size="sm"
                            style={{ alignSelf: 'flex-start' }}
                            icon="DataExchangeInquiries"
                            variant="ghost"
                            aria-label={`Open Vehicles ${getUniversalSearchFormLabel(
                                enabledFeatures.dexNextEnabled
                            )}`}
                            onClick={handleOpenInDexButtonClick}
                            data-testid="OPEN_VEHICLE_IN_DEX"
                            tabIndex={-1}
                        />
                    )}
                    {showDeleteButton ? (
                        <FormInputIconButton
                            style={{ alignSelf: 'flex-start' }}
                            size="sm"
                            aria-label="Delete"
                            variant="ghost"
                            icon="TrashCan"
                            onClick={onDelete}
                            data-testid={`vehicle-remove-${vehicleId}`}
                            tabIndex={-1}
                        />
                    ) : undefined}
                </HStack>
            </HStack>
        );
    }
);
